/* global Office, localStorage, crypto, TextEncoder, btoa, atob, fetch, URLSearchParams */

import { authConfig, redirectUri } from "./authConfig";

/**
 * Office Add-in authentication via Auth0 using the Authorization Code + PKCE flow,
 * driven through the Office Dialog API.
 *
 * Why the Dialog API: redirect-based OAuth is unreliable inside the add-in's
 * embedded webview. Instead we open Auth0's hosted login in an Office dialog; the
 * final redirect lands on `auth-end.html` (our own origin), which posts the auth
 * code back to the task pane via `Office.context.ui.messageParent`.
 *
 * Token storage: the resulting access token lives in `localStorage`. The task pane
 * and the custom-functions bundle are separate webpack entries, but they run in the
 * same SharedRuntime, so `localStorage` is the reliable channel they both read.
 */

const TOKEN_KEY = "sheetybanks.auth.token";
const EXPIRY_KEY = "sheetybanks.auth.expiry";
const USER_KEY = "sheetybanks.auth.user";
const VERIFIER_KEY = "sheetybanks.auth.pkce_verifier";
const STATE_KEY = "sheetybanks.auth.state";

export interface AuthUser {
  sub: string;
  email?: string;
  name?: string;
}

// --- PKCE helpers ---------------------------------------------------------

function randomString(bytes = 32): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr.buffer);
}

function base64UrlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest("SHA-256", data);
}

// Decode a JWT payload (no signature check — the API validates the token).
function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// --- Token storage --------------------------------------------------------

/** Returns the cached access token if present and not expired, else null. */
export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = Number(localStorage.getItem(EXPIRY_KEY) || 0);
  if (!token || Date.now() >= expiry) {
    return null;
  }
  return token;
}

export function getUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as AuthUser) : null;
}

export function isSignedIn(): boolean {
  return getToken() !== null;
}

/** Clears the locally cached token/profile. Does NOT touch the Auth0 session. */
function clearLocal(): void {
  [TOKEN_KEY, EXPIRY_KEY, USER_KEY, VERIFIER_KEY, STATE_KEY].forEach((k) =>
    localStorage.removeItem(k)
  );
}

/**
 * Full sign-out: clears the local token AND ends the Auth0 session.
 *
 * Clearing localStorage alone leaves Auth0's SSO cookie intact, so the next sign-in
 * silently re-authenticates the same user with no password prompt. To truly log out
 * (e.g. to switch users), we also navigate an Office dialog to Auth0's `/v2/logout`,
 * which clears that cookie and redirects to `returnTo` — our own `auth-end.html`,
 * which messageParents back so we can close the dialog.
 *
 * NOTE: the `returnTo` URL must be listed in the Auth0 application's
 * "Allowed Logout URLs" (separate from Allowed Callback URLs), else Auth0 errors.
 */
export async function signOut(): Promise<void> {
  // Clear locally first so the UI reflects signed-out immediately, even if the
  // dialog fails to open.
  clearLocal();

  const logoutUrl =
    `https://${authConfig.domain}/v2/logout?` +
    new URLSearchParams({
      client_id: authConfig.clientId,
      returnTo: redirectUri(),
    }).toString();

  await new Promise<void>((resolve) => {
    Office.context.ui.displayDialogAsync(
      logoutUrl,
      { height: 40, width: 30, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          // Local token is already gone; best-effort on the remote session.
          resolve();
          return;
        }
        const dlg = asyncResult.value;
        const finish = () => {
          try {
            dlg.close();
          } catch {
            /* already closed */
          }
          resolve();
        };
        // auth-end.html posts back once Auth0 has redirected to it (session cleared).
        dlg.addEventHandler(Office.EventType.DialogMessageReceived, finish);
        // Or the user closed the dialog manually.
        dlg.addEventHandler(Office.EventType.DialogEventReceived, finish);
      }
    );
  });
}

function storeToken(accessToken: string, idToken: string | undefined, expiresInSec: number): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  // Refresh a minute early to avoid edge-of-expiry 401s.
  localStorage.setItem(EXPIRY_KEY, String(Date.now() + (expiresInSec - 60) * 1000));
  if (idToken) {
    const claims = decodeJwt(idToken);
    const user: AuthUser = {
      sub: String(claims.sub ?? ""),
      email: claims.email as string | undefined,
      name: claims.name as string | undefined,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

// --- Sign-in flow ---------------------------------------------------------

let dialog: Office.Dialog | null = null;

/**
 * Opens the Auth0 login dialog and resolves with the signed-in user once the
 * authorization code has been exchanged for tokens. Rejects on error/cancel.
 */
export async function signIn(): Promise<AuthUser> {
  const verifier = randomString();
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = randomString(16);

  localStorage.setItem(VERIFIER_KEY, verifier);
  localStorage.setItem(STATE_KEY, state);

  const authorizeUrl =
    `https://${authConfig.domain}/authorize?` +
    new URLSearchParams({
      response_type: "code",
      client_id: authConfig.clientId,
      redirect_uri: redirectUri(),
      scope: authConfig.scope,
      audience: authConfig.audience,
      code_challenge: challenge,
      code_challenge_method: "S256",
      state,
    }).toString();

  return new Promise<AuthUser>((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      authorizeUrl,
      { height: 60, width: 40, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(asyncResult.error.message));
          return;
        }
        dialog = asyncResult.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          // Exchange the code and persist the token BEFORE closing the dialog.
          // On Windows desktop, dialog.close() can crash the WebView2 process and
          // reload the whole task pane (OfficeDev/office-js#6309); with the token
          // already in localStorage the reloaded pane comes back signed in.
          try {
            const { message } = arg as { message: string };
            const data = JSON.parse(message) as { code?: string; state?: string; error?: string };

            if (data.error) throw new Error(data.error);
            if (!data.code) throw new Error("No authorization code returned");
            if (data.state !== localStorage.getItem(STATE_KEY)) {
              throw new Error("State mismatch — possible CSRF, aborting");
            }

            const user = await exchangeCodeForToken(data.code);
            closeDialog();
            resolve(user);
          } catch (err) {
            closeDialog();
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          // 12006 = user closed the dialog manually.
          closeDialog();
          reject(new Error("Login dialog was closed"));
        });
      }
    );
  });
}

function closeDialog(): void {
  if (dialog) {
    dialog.close();
    dialog = null;
  }
}

async function exchangeCodeForToken(code: string): Promise<AuthUser> {
  const verifier = localStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error("Missing PKCE verifier");

  const res = await fetch(`https://${authConfig.domain}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: authConfig.clientId,
      code_verifier: verifier,
      code,
      redirect_uri: redirectUri(),
    }).toString(),
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${detail}`);
  }

  const tokens = (await res.json()) as {
    access_token: string;
    id_token?: string;
    expires_in: number;
  };

  localStorage.removeItem(VERIFIER_KEY);
  localStorage.removeItem(STATE_KEY);
  storeToken(tokens.access_token, tokens.id_token, tokens.expires_in);

  const user = getUser();
  if (!user) throw new Error("Signed in but no user profile found");
  return user;
}

/** Convenience: build Authorization headers for an authenticated API call. */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
